import { useEffect, useState } from "react";
import { Sparkles, Loader2, Zap } from "lucide-react";

/**
 * Auto-fulfillment ETA chip — shown to clients on pending/in_progress requests that the
 * background `auto_fulfill_poller` will pick up at `auto_fulfill_at`.
 *
 * States:
 *   • Pro lane (auto_fulfill_lane==='pro')  → "Pro lane · starting now" (amber Zap icon)
 *   • Free + future auto_fulfill_at         → "Analyst · ETA 14:32" (live countdown)
 *   • Free + past auto_fulfill_at           → "Up next" (poller will claim within 60s)
 *   • In progress                           → "Drafting now…" (spinner, claimed by poller)
 *
 * Hidden for admin views (admins see EmailQueueChip + manual fulfill controls instead)
 * and for any request without auto_fulfill set.
 */
export default function AutoFulfillEtaChip({ request, onChange }) {
    const [now, setNow] = useState(Date.now());

    // Tick once per second while we're counting down so the ETA stays live.
    useEffect(() => {
        if (!request.auto_fulfill) return;
        if (request.status === "completed" || request.status === "declined") return;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [request.auto_fulfill, request.status]);

    // When the countdown crosses zero, gently nudge the parent to refetch so the chip
    // can flip from "ETA 0:00" → "Drafting now…" within a couple seconds of the poller tick.
    useEffect(() => {
        if (!request.auto_fulfill || !request.auto_fulfill_at) return;
        if (request.status !== "pending") return;
        const due = new Date(request.auto_fulfill_at).getTime();
        const ms = Math.max(0, due - Date.now()) + 5000;
        const id = setTimeout(() => onChange && onChange(), ms);
        return () => clearTimeout(id);
    }, [request.auto_fulfill, request.auto_fulfill_at, request.status, onChange]);

    if (!request.auto_fulfill) return null;
    if (request.status === "completed" || request.status === "declined") return null;

    // State: AI is actively drafting (poller has claimed it).
    if (request.status === "in_progress") {
        return (
            <span
                className="inline-flex items-center gap-1.5 border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-300"
                data-testid={`auto-fulfill-drafting-${request.id}`}
                title="Your AI analyst is generating your dossier right now."
            >
                <Loader2 className="w-3 h-3 animate-spin" /> Drafting now…
            </span>
        );
    }

    // State: Pro lane — no countdown, just an instant-start signal.
    if (request.auto_fulfill_lane === "pro") {
        return (
            <span
                className="inline-flex items-center gap-1.5 border border-amber-500/60 bg-amber-500/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-300"
                data-testid={`auto-fulfill-pro-${request.id}`}
                title="Pro lane — your request skipped the queue and will fire on the next poller tick (≤60s)."
            >
                <Zap className="w-3 h-3" /> Pro lane · starting now
            </span>
        );
    }

    // State: pending — counting down to auto_fulfill_at.
    if (!request.auto_fulfill_at) return null;
    const due = new Date(request.auto_fulfill_at).getTime();
    const remainMs = Math.max(0, due - now);
    const mm = Math.floor(remainMs / 60000);
    const ss = Math.floor((remainMs % 60000) / 1000);

    if (remainMs <= 0) {
        return (
            <span
                className="inline-flex items-center gap-1.5 border border-emerald-500/50 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-300"
                data-testid={`auto-fulfill-upnext-${request.id}`}
                title="Your request is next in the queue — your analyst will start drafting shortly."
            >
                <Sparkles className="w-3 h-3" /> Up next
            </span>
        );
    }

    return (
        <span
            className="inline-flex items-center gap-1.5 border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber-300"
            data-testid={`auto-fulfill-eta-${request.id}`}
            title={`Your AI analyst will start drafting at ${new Date(due).toLocaleTimeString()}`}
        >
            <Sparkles className="w-3 h-3" />
            Analyst · ETA {mm}:{String(ss).padStart(2, "0")}
        </span>
    );
}
